<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'

import appEnv from '@/support/env'

import { useAccount, useAccountStore } from '@/apps/adok/entities/account'

/**
 * Loaded on mount **and** on a watch: `widgetEnabled` turns true when the account finishes
 * loading, which is usually after this component has mounted.
 */
const { widgetEnabled, widgetData, isStaff } = useAccount()

const accountStore = useAccountStore()

const domain = appEnv.WIDGET_DOMAIN

const appKey = appEnv.WIDGET_KEY

const canLoad = computed(
  () => widgetEnabled.value && !!domain && !!appKey && !isStaff.value
)

let loaded = false

function loadMessenger() {
  if (loaded) return
  loaded = true

  // `widgetData` is already JSON, so it goes in unquoted — and it must stay
  // server-produced: this lands inside a script body, where a closing script tag from
  // anywhere would end the tag early. Writing that tag out even inside a comment breaks
  // this SFC's parse.
  const code = `
    (function(d,t) {
      var g=d.createElement(t),s=d.getElementsByTagName(t)[0];
      g.src='https://${domain}/embed.js'
      s.parentNode.insertBefore(g,s);
      g.onload=function(){
        window.widget = new window.ChaskiqMessengerEncrypted({
          domain: "https://${domain}",
          ws:  "wss://${domain}/cable",
          app_id: '${appKey}',
          data: ${widgetData.value},
          lang: "en",
          open: ${new URLSearchParams(window.location.search).has('open_widget')}
        })
        window.dispatchEvent(new CustomEvent('widget-ready'));
      }
    })(document,"script");
  `

  // The messenger builds its UI on `window`, outside Vue, so this event is the only thing
  // that can say it is usable. `{ once: true }`: a widget that re-announces itself must not
  // stack listeners.
  window.addEventListener(
    'widget-ready',
    () => accountStore.markSupportWidgetReady(),
    { once: true }
  )

  setTimeout(() => {
    const script = document.createElement('script')
    script.text = code
    document.head.appendChild(script)
  })
}

onMounted(() => {
  if (canLoad.value) loadMessenger()
})

watch(canLoad, (ready) => {
  if (ready) loadMessenger()
})
</script>

<template>
  <div class="support-widget" />
</template>

<style lang="scss" scoped>
.support-widget {
  display: none;
}
</style>
